<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { layerRows } from '@/model/layerTree'
import { useI18n } from '@/i18n'
import * as actions from '@/app/actions'
import { BLEND_MODES, type BlendMode, type GroupBlendMode } from '@/types/document'
import { blendModeLabel } from '@/components/common/blendModeLabels'
import { thumbnailUrl, thumbVersion } from '@/composables/useThumbnails'
import { useUiStore } from '@/stores/uiStore'

const docStore = useDocumentStore()
const ui = useUiStore()
const { t } = useI18n()

const rows = computed(() => (docStore.doc ? layerRows(docStore.doc) : []))
const active = computed(() => docStore.activeLayer)
const editingId = ref<string | null>(null)
const editingName = ref('')

function thumb(pixelId: string | undefined): string | null {
  void thumbVersion.value
  void docStore.historyVersion
  return pixelId ? thumbnailUrl(pixelId) : null
}

function select(id: string, e: MouseEvent) {
  const cur = docStore.doc?.activeLayerIds ?? []
  if (e.metaKey || e.ctrlKey) {
    docStore.setActiveLayers(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id])
  } else if (e.shiftKey && cur.length) {
    // 表示順で範囲選択
    const ids = rows.value.map(r => r.id)
    const a = ids.indexOf(cur[cur.length - 1]!)
    const b = ids.indexOf(id)
    const [lo, hi] = a < b ? [a, b] : [b, a]
    docStore.setActiveLayers(ids.slice(lo, hi + 1))
  } else {
    docStore.setActiveLayers([id])
  }
}
function startRename(id: string, name: string) {
  editingId.value = id
  editingName.value = name
}
function commitRename() {
  const id = editingId.value
  if (id && docStore.doc?.layers[id] && editingName.value.trim() && editingName.value !== docStore.doc.layers[id]!.name) {
    docStore.setLayerProps(id, { name: editingName.value.trim() }, 'history.renameLayer')
  }
  editingId.value = null
}
let opacityTx = false
function onOpacityInput(e: Event) {
  if (!active.value) return
  if (!opacityTx) {
    docStore.beginTransaction('history.layerProps')
    opacityTx = true
  }
  docStore.setLayerProps(active.value.id, { opacity: Math.max(0, Math.min(1, Number((e.target as HTMLInputElement).value) / 100)) })
}
function onOpacityChange() {
  if (opacityTx) {
    docStore.commitTransaction()
    opacityTx = false
  }
}
function setBlend(e: Event) {
  const v = (e.target as HTMLSelectElement).value as BlendMode | GroupBlendMode
  if (active.value) docStore.setLayerProps(active.value.id, { blendMode: v })
}
const blendOptions = computed<(BlendMode | GroupBlendMode)[]>(() => (active.value?.kind === 'group' ? ['pass-through', ...BLEND_MODES] : [...BLEND_MODES]))

// ドラッグで順序変更
const dragId = ref<string | null>(null)
const overId = ref<string | null>(null)
const overInto = ref(false)
function onDragStart(id: string, e: DragEvent) {
  dragId.value = id
  e.dataTransfer?.setData('text/plain', id)
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
}
function onDragOver(id: string, e: DragEvent) {
  if (!dragId.value || dragId.value === id) return
  e.preventDefault()
  overId.value = id
  const l = docStore.doc?.layers[id]
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  // グループ行の中央 60% に落とすとグループの中へ
  overInto.value = l?.kind === 'group' && e.clientY > r.top + r.height * 0.2 && e.clientY < r.bottom - r.height * 0.2
}
function onDrop(id: string) {
  const src = dragId.value
  const into = overInto.value
  dragId.value = null
  overId.value = null
  overInto.value = false
  if (!src || src === id || !docStore.doc) return
  const doc = docStore.doc
  const target = doc.layers[id]
  if (into && target?.kind === 'group') {
    docStore.reorderLayer(src, id, target.children.length)
    return
  }
  const parentOf = (lid: string): string | null => {
    if (doc.rootChildren.includes(lid)) return null
    for (const l of Object.values(doc.layers)) if (l.kind === 'group' && l.children.includes(lid)) return l.id
    return null
  }
  const parent = parentOf(id)
  const siblings = parent === null ? doc.rootChildren : (doc.layers[parent] as { children: string[] }).children
  let index = siblings.indexOf(id)
  if (parentOf(src) === parent && siblings.indexOf(src) < index) index -= 1
  docStore.reorderLayer(src, parent, index + 1)
}
function onDragEnd() {
  dragId.value = null
  overId.value = null
  overInto.value = false
}
function isLocked(l: { lock: { all: boolean; position: boolean; pixels: boolean; transparency: boolean } }) {
  return l.lock.all || l.lock.position || l.lock.pixels || l.lock.transparency
}
</script>

<template>
  <div class="panel">
    <div class="panel-title row">
      <span>{{ t('panel.layers') }}</span>
      <span class="spacer" />
      <button class="icon ghost" :title="t('menu.layer.addMaskReveal')" :disabled="!active || !!active.mask" @click="active && docStore.addMask(active.id, 'reveal')">◐</button>
      <button class="icon ghost" :title="t('menu.layer.group')" :disabled="!active" @click="actions.groupActiveLayers()">▣</button>
      <button class="icon ghost" :title="t('layer.new')" :disabled="!docStore.hasDocument" @click="docStore.addEmptyLayer()">＋</button>
      <button class="icon ghost" :title="t('layer.delete')" :disabled="!active" @click="actions.deleteActiveLayer()">🗑</button>
    </div>
    <div v-if="active" class="controls">
      <select class="blend" :value="active.blendMode" @change="setBlend">
        <option v-for="m in blendOptions" :key="m" :value="m">{{ blendModeLabel(m) }}</option>
      </select>
      <label class="row opacity">
        <span class="muted">{{ t('layer.opacity') }}</span>
        <input type="range" min="0" max="100" step="1" :value="Math.round(active.opacity * 100)" @input="onOpacityInput" @change="onOpacityChange" />
        <span class="mono val">{{ Math.round(active.opacity * 100) }}%</span>
      </label>
    </div>
    <div class="list">
      <div v-if="rows.length === 0" class="empty muted">{{ t('layer.empty') }}</div>
      <div
        v-for="r in rows"
        :key="r.id"
        class="layer"
        :class="{ active: docStore.doc?.activeLayerIds.includes(r.id), over: overId === r.id && !overInto, into: overId === r.id && overInto, hidden: !r.layer.visible, clipped: r.layer.clipToBelow }"
        :style="{ paddingLeft: 4 + r.depth * 14 + 'px' }"
        draggable="true"
        @click="select(r.id, $event)"
        @dblclick="startRename(r.id, r.layer.name)"
        @dragstart="onDragStart(r.id, $event)"
        @dragover="onDragOver(r.id, $event)"
        @drop="onDrop(r.id)"
        @dragend="onDragEnd"
      >
        <button class="eye ghost" :title="t('layer.visible')" @click.stop="docStore.toggleVisible(r.id)">{{ r.layer.visible ? '👁' : '·' }}</button>
        <button v-if="r.layer.kind === 'group'" class="expand ghost" @click.stop="docStore.setGroupExpanded(r.id, !r.layer.expanded)">{{ r.layer.expanded ? '▾' : '▸' }}</button>
        <span v-else-if="r.layer.clipToBelow" class="cliparrow" :title="t('layer.clipped')">↳</span>
        <span class="thumb" :class="r.layer.kind">
          <img v-if="r.layer.kind === 'raster' && thumb(r.layer.pixelId)" :src="thumb(r.layer.pixelId)!" alt="" draggable="false" />
          <span v-else-if="r.layer.kind === 'group'" class="glyph">▣</span>
          <span v-else-if="r.layer.kind === 'adjustment'" class="glyph adj">◐</span>
          <span v-else-if="r.layer.kind === 'text'" class="glyph text">T</span>
          <template v-else-if="r.layer.kind === 'smart'"><img v-if="thumb(r.layer.sourceId)" :src="thumb(r.layer.sourceId)!" alt="" draggable="false" /><span class="badge" :title="t('layer.smart')">◈</span></template>
          <span v-else-if="r.layer.kind === 'shape'" class="glyph shape" :style="{ background: r.layer.fillHex ?? 'transparent', borderColor: r.layer.strokeHex ?? 'var(--fg-2)' }" />
          <span v-else-if="r.layer.kind === 'fill'" class="glyph fill" :style="{ background: r.layer.fill.kind === 'solid' ? r.layer.fill.hex : r.layer.fill.kind === 'gradient' ? `linear-gradient(90deg, ${r.layer.fill.stops[0]?.hex}, ${r.layer.fill.stops[r.layer.fill.stops.length - 1]?.hex})` : 'repeating-conic-gradient(#888 0 25%, #ccc 0 50%) 0 0 / 8px 8px' }" />
        </span>
        <span
          v-if="r.layer.mask"
          class="thumb mask"
          :class="{ disabled: !r.layer.mask.enabled }"
          :title="t('layer.mask')"
          @click.stop="docStore.setMaskEnabled(r.id, !r.layer.mask!.enabled)"
        >
          <img v-if="thumb(r.layer.mask.pixelId)" :src="thumb(r.layer.mask.pixelId)!" alt="" draggable="false" />
        </span>
        <input
          v-if="editingId === r.id"
          v-model="editingName"
          class="rename"
          autofocus
          @blur="commitRename"
          @keydown.enter.prevent="commitRename"
          @keydown.esc.prevent="editingId = null"
          @click.stop
        />
        <span v-else class="name">{{ r.layer.name }}</span>
        <span v-if="r.layer.styles && Object.keys(r.layer.styles).length" class="fx" :title="t('menu.layer.style')" @click.stop="docStore.setActiveLayers([r.id]); ui.dialog = 'layerStyle'">fx</span>
        <span v-if="isLocked(r.layer)" class="lock" :title="t('layer.lockedHint')">🔒</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.controls {
  padding: 6px 8px;
  border-bottom: 1px solid var(--line);
  background: var(--bg-2);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.blend {
  width: 100%;
  min-width: 0;
}
.opacity {
  gap: 6px;
}
.opacity input {
  flex: 1;
  min-width: 0;
}
.val {
  width: 36px;
  text-align: right;
  font-size: 11px;
}
.list {
  overflow: auto;
  flex: 1;
  min-height: 0;
}
.empty {
  padding: 16px;
  text-align: center;
}
.layer {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  padding-right: 6px;
  border-bottom: 1px solid var(--line-weak);
  cursor: default;
}
.layer:hover {
  background: var(--bg-hover);
}
.layer.active {
  background: var(--accent-dim);
}
.layer.over {
  box-shadow: inset 0 2px 0 var(--accent);
}
.layer.into {
  box-shadow: inset 0 0 0 2px var(--accent);
}
.layer.hidden .name {
  color: var(--fg-3);
}
.eye,
.expand {
  width: 18px;
  height: 20px;
  padding: 0;
  font-size: 11px;
  flex: none;
}
.cliparrow {
  width: 18px;
  text-align: center;
  color: var(--fg-2);
  flex: none;
}
.thumb {
  width: 34px;
  height: 26px;
  border: 1px solid var(--line-strong);
  background: repeating-conic-gradient(var(--checker-a) 0 25%, var(--checker-b) 0 50%) 0 0 / 8px 8px;
  border-radius: 2px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.thumb img {
  max-width: 100%;
  max-height: 100%;
  image-rendering: auto;
}
.thumb.shape {
  background: var(--bg-3);
}
.glyph.shape {
  width: 18px;
  height: 14px;
  border: 2px solid;
  border-radius: 2px;
}
.thumb.group,
.thumb.adjustment,
.thumb.text {
  background: var(--bg-3);
  color: var(--fg-2);
}
.thumb.text .text {
  font-weight: 700;
  font-size: 16px;
  color: var(--fg-1);
}
.thumb.adjustment .adj {
  color: var(--accent-hi);
  font-size: 16px;
}
.thumb.fill {
  background: var(--bg-3);
}
.glyph.fill {
  width: 100%;
  height: 100%;
  display: block;
}
.thumb.mask {
  background: #000;
  border-color: var(--fg-2);
}
.thumb.mask.disabled {
  opacity: 0.4;
  border-color: var(--danger);
}
.layer.clipped .thumb:first-of-type {
  margin-left: 0;
}
.name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rename {
  flex: 1;
  min-width: 0;
  padding: 1px 4px;
}
.lock {
  font-size: 10px;
  opacity: 0.7;
}
.fx {
  font-size: 10px;
  font-style: italic;
  color: var(--accent-hi);
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  padding: 0 3px;
  cursor: pointer;
}
.thumb .badge {
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: 9px;
  line-height: 1;
  background: var(--bg-0);
  color: var(--accent);
  padding: 1px 2px;
}
.thumb {
  position: relative;
}
</style>
